import React, { useState, useRef, useEffect } from 'react';
import { Box, Paper, Divider, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Alert, Typography, Stack, Chip, FormHelperText, Popper, ClickAwayListener, MenuList, Tooltip } from '@mui/material';
import {
  Add,
  Remove,
  CallMerge,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Delete,
  ContentCopy,
  ContentPaste,
  Undo,
  Redo,
  AutoAwesome,
  ChevronRight,
  Palette,
  TableChart,
  Info,
  Help,
  InsertPageBreak,
  Save,
  Code,
  DragIndicator,
  Settings
} from '@mui/icons-material';
import EditorToolbar from './EditorToolbar';
import EditorContent from './EditorContent';
import ImageUploadModal from './modals/ImageUploadModal';
import AdvancedTableEditor from './modals/AdvancedTableEditor';
import ImageResizeModal from './modals/ImageResizeModal';
import templateService from '../../services/templateService';
import { env } from '@/utils/env';
import TablePropertiesModal, { TableProperties } from './modals/TablePropertiesModal';
import AiModal from './modals/AiModal';
import HelpModal from './modals/HelpModal';
import CellPropertiesModal from './modals/CellPropertiesModal';
import { useTableResize, cleanExistingTableWidths, updateTableColumnWidths } from './useTableResize';
import DOMPurify from 'dompurify';
import './CustomTextEditor.css';

export interface EditorState {
  content: string;
  selectedText: string;
  selection: { start: number; end: number } | null;
}

export interface FontStyle {
  fontFamily: string;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  subscript?: boolean;
  superscript?: boolean;
  color?: string;
  backgroundColor?: string;
  textAlign: 'left' | 'center' | 'right' | 'justify';
}

interface SysEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  height?: number;
  disabled?: boolean;
  showVariables?: boolean;
  variablesType?: string;
  loadingVariables?: boolean;
  onSave?: (content?: string) => void;
}

const SysEditor: React.FC<SysEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Digite seu texto aqui...',
  height = 400,
  disabled = false,
  showVariables = false,
  variablesType = '',
  loadingVariables = false,
  onSave
}) => {
  // Configuração padrão do DOMPurify para o editor
  const sanitizeConfig = {
    ADD_ATTR: ['data-custom-width', 'data-custom-height', 'data-table-align', 'data-label', 'contenteditable', 'style']
  };

  const [editorState, setEditorState] = useState<EditorState>({
    content: value ? DOMPurify.sanitize(value, sanitizeConfig) : '',
    selectedText: '',
    selection: null
  });

  const [fontStyle, setFontStyle] = useState<FontStyle>({
    fontFamily: 'Arial',
    fontSize: 14,
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    subscript: false,
    superscript: false,
    textAlign: 'left'
  });

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSourceCode, setIsSourceCode] = useState(false);

  const [currentParagraphStyle, setCurrentParagraphStyle] = useState('p');
  const [currentListType, setCurrentListType] = useState('decimal');
  const [paperSize, setPaperSize] = useState('auto');

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [isImageResizeModalOpen, setIsImageResizeModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);

  const [tableContextMenu, setTableContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    table: HTMLTableElement;
    cell: HTMLTableCellElement;
    row: number;
    col: number;
  } | null>(null);

  const [hoveredSubmenu, setHoveredSubmenu] = useState<string | null>(null);
  const [submenuAnchor, setSubmenuAnchor] = useState<{
    element: HTMLElement | null;
    type: string;
  }>({ element: null, type: '' });
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [copiedTableCell, setCopiedTableCell] = useState<{
    content: string;
    backgroundColor?: string;
    textAlign?: string;
  } | null>(null);


  // Estados para desfazer/refazer
  const [history, setHistory] = useState<string[]>([value]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [lastSavedContent, setLastSavedContent] = useState(value);
  const [typingTimer, setTypingTimer] = useState<NodeJS.Timeout | null>(null);

  // Estados para OpenAI
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Estado para modal de ajuda
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  // Estado para modal de propriedades da célula
  const [isCellPropertiesModalOpen, setIsCellPropertiesModalOpen] = useState(false);


  // Estado para modal flexível de propriedades da tabela
  const [isTablePropertiesModalOpen, setIsTablePropertiesModalOpen] = useState(false);

  // Estados para variáveis (similar ao HtmlEditor)
  const [draggedVariable, setDraggedVariable] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [internalVariables, setInternalVariables] = useState<string[]>([]);
  const [internalLoadingVariables, setInternalLoadingVariables] = useState(false);
  const [savePressed, setSavePressed] = useState(false);

  const editorRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);

  const saveCurrentRange = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedRangeRef.current = selection.getRangeAt(0).cloneRange();
    }
  };

  useEffect(() => {
    if (value !== editorState.content) {
      setEditorState(prev => ({ ...prev, content: value ? DOMPurify.sanitize(value, sanitizeConfig) : '' }));
    }
  }, [value]);

  // Carregar variáveis quando variablesType é fornecido
  useEffect(() => {
    if (showVariables && variablesType) {
      loadAvailableVariables();
    }
  }, [showVariables, variablesType]);



  const loadAvailableVariables = async () => {
    if (!variablesType) return;

    setInternalLoadingVariables(true);
    try {
      const response = await templateService.getAvailableVariables(variablesType);
      setInternalVariables(response.variables);
    } catch (error) {
      console.error('🚨 SysEditor - Erro ao carregar variáveis:', error);
      setInternalVariables([]);
    } finally {
      setInternalLoadingVariables(false);
    }
  };

  // Determinar quais variáveis usar (carregadas internamente)
  const currentVariables = internalVariables;
  const currentLoadingVariables = internalLoadingVariables;

  const addToHistory = (content: string) => {
    if (content !== lastSavedContent) {
      // Se o conteúdo atual for muito grande (ex: contém base64 gigante), não salvamos tantas instâncias
      const MAX_HISTORY_LENGTH = content.length > 500000 ? 5 : 50; 

      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(content);

      // Limitar histórico baseado no peso do conteúdo
      if (newHistory.length > MAX_HISTORY_LENGTH) {
        // Remover os mais antigos até chegar no limite
        newHistory.splice(0, newHistory.length - MAX_HISTORY_LENGTH);
      }
      
      // Ajustar o índice para apontar para o final do novo array cortado
      setHistoryIndex(newHistory.length - 1);

      setHistory(newHistory);
      setLastSavedContent(content);
    }
  };

  const handleContentChange = (newContent: string, forceAddToHistory: boolean = false) => {
    // Remover classes de seleção visual antes de guardar no estado/banco
    const cleanContent = newContent.replace(/\s*syseditor-selected-cell\s*/g, '');

    // console.log('[SysEditor] handleContentChange disparado. forceAddToHistory:', forceAddToHistory);
    // console.log('[SysEditor] Tamanho original:', newContent.length, 'Tamanho limpo:', cleanContent.length);

    setEditorState(prev => ({ ...prev, content: cleanContent }));
    onChange?.(cleanContent);

    // Se for uma mudança forçada (como inserção de imagem/tabela), adicionar imediatamente
    if (forceAddToHistory) {
      addToHistory(cleanContent);
      return;
    }

    // Limpar timer anterior se existir
    if (typingTimer) {
      clearTimeout(typingTimer);
    }

    // Detectar se foi uma mudança significativa (espaço, enter, pontuação)
    const isSignificantChange =
      newContent.length === 0 || // Conteúdo foi limpo
      lastSavedContent.length === 0 || // Era vazio e agora tem conteúdo
      Math.abs(newContent.length - lastSavedContent.length) > 10 || // Mudança grande (paste/delete)
      /[\s\.\,\!\?\;\:\n]$/.test(newContent) && !(/[\s\.\,\!\?\;\:\n]$/.test(lastSavedContent)); // Terminou com pontuação/espaço

    if (isSignificantChange) {
      // Adicionar imediatamente ao histórico
      addToHistory(newContent);
    } else {
      // Configurar timer para adicionar após pausa na digitação (1 segundo)
      const timer = setTimeout(() => {
        addToHistory(newContent);
      }, 1000);
      setTypingTimer(timer);
    }
  };

  const detectCurrentFormatting = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      // Pegar o elemento pai da seleção ou cursor
      let element: Node | HTMLElement | null = range.commonAncestorContainer;

      // Se for um nó de texto, pegar o elemento pai
      if (element.nodeType === Node.TEXT_NODE) {
        element = (element as Text).parentElement;
      }

      // Se ainda não temos um HTMLElement, tentar pegar o elemento no ponto do cursor
      if (!element || !(element instanceof HTMLElement)) {
        // Tentar pegar o elemento no startContainer
        element = range.startContainer;
        if (element.nodeType === Node.TEXT_NODE) {
          element = (element as Text).parentElement;
        }
      }

      // Se ainda não conseguimos, usar o próprio editor como fallback
      if (!element || !(element instanceof HTMLElement)) {
        element = editorRef.current;
      }

      if (element && element instanceof HTMLElement) {
        // Detectar formatação atual
        const computedStyle = window.getComputedStyle(element);
        const currentFontSize = parseInt(computedStyle.fontSize) || 14;
        const currentFontFamily = computedStyle.fontFamily.replace(/['"]/g, '').split(',')[0] || 'Arial';
        const currentBold = computedStyle.fontWeight === 'bold' || parseInt(computedStyle.fontWeight) >= 600;
        const currentItalic = computedStyle.fontStyle === 'italic';

        // Detectar estilo do parágrafo
        let blockElement: HTMLElement | null = element;
        while (blockElement && !blockElement.tagName?.match(/^(P|DIV|H[1-6]|BLOCKQUOTE|PRE)$/)) {
          blockElement = blockElement.parentElement;
        }

        if (blockElement) {
          const tagName = blockElement.tagName.toLowerCase();
          setCurrentParagraphStyle(tagName);

          // Detectar tipo de lista se estiver em uma lista ordenada
          if (tagName === 'li') {
            const parentList = blockElement.closest('ol');
            if (parentList) {
              const listStyleType = parentList.style.listStyleType || 'decimal';
              setCurrentListType(listStyleType);
            }
          }
        }

        setFontStyle(prev => ({
          ...prev,
          fontSize: currentFontSize,
          fontFamily: currentFontFamily,
          bold: currentBold,
          italic: currentItalic
        }));
      }
    }
  };

  const handleSelectionChange = (selectedText: string, selection: { start: number; end: number } | null) => {
    setEditorState(prev => ({ ...prev, selectedText, selection }));

    // Sempre detectar formatação atual (mesmo sem seleção, para detectar no cursor)
    detectCurrentFormatting();
  };

  const applyFormat = (command: string, value?: string) => {
    if (disabled) return;

    const alignmentMap: Record<string, string> = {
      'justifyLeft': 'left',
      'justifyCenter': 'center',
      'justifyRight': 'right',
      'justifyFull': 'justify',
    };

    if (alignmentMap[command]) {
      const selection = window.getSelection();
      const anchorNode = selection?.anchorNode;
      const cell = anchorNode instanceof HTMLElement
        ? anchorNode.closest('td, th')
        : anchorNode?.parentElement?.closest('td, th');

      if (cell) {
        (cell as HTMLElement).style.textAlign = alignmentMap[command];
        if (editorRef.current) {
          handleContentChange(editorRef.current.innerHTML, true);
        }
        return;
      }
    }

    document.execCommand(command, false, value);

    switch (command) {
      case 'bold':
        setFontStyle(prev => ({ ...prev, bold: !prev.bold }));
        break;
      case 'italic':
        setFontStyle(prev => ({ ...prev, italic: !prev.italic }));
        break;
      case 'underline':
        setFontStyle(prev => ({ ...prev, underline: !prev.underline }));
        break;
      case 'strikeThrough':
        setFontStyle(prev => ({ ...prev, strikethrough: !prev.strikethrough }));
        break;
      case 'subscript':
        setFontStyle(prev => ({ ...prev, subscript: !prev.subscript, superscript: false }));
        break;
      case 'superscript':
        setFontStyle(prev => ({ ...prev, superscript: !prev.superscript, subscript: false }));
        break;
      case 'foreColor':
        setFontStyle(prev => ({ ...prev, color: value }));
        break;
      case 'hiliteColor':
      case 'backColor':
        setFontStyle(prev => ({ ...prev, backgroundColor: value }));
        break;
    }

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML, true);
    }
  };

  // Função utilitária para arredondar valores de porcentagem
  const roundPercentage = (value: number): number => {
    return Math.round(value * 100) / 100;
  };

  const {
    isResizingColumn,
    isResizingRow,
    isResizingTable,
    resizingColumnData,
    resizingRowData,
    resizingTableData,
    handleColumnResizeStart,
    handleColumnResizeMove,
    handleColumnResizeEnd,
    handleRowResizeStart,
    handleRowResizeMove,
    handleRowResizeEnd,
    handleTableResizeStart,
    handleTableResizeMove,
    handleTableResizeEnd,
  } = useTableResize({
    editorRef,
    onContentChange: handleContentChange,
  });

  const addResizeHandles = (table: HTMLTableElement) => {
    // Limpar larguras não arredondadas existentes
    cleanExistingTableWidths(table);

    const tableAlign = table.getAttribute('data-table-align') || 'center';

    // Configurar tabela
    table.style.position = 'relative';
    table.style.height = 'auto';
    table.style.tableLayout = 'fixed';

    // Validar e aplicar largura customizada
    const rawCustomWidth = table.getAttribute('data-custom-width');
    const customWidthNum = rawCustomWidth ? parseFloat(rawCustomWidth) : NaN;

    if (!isNaN(customWidthNum) && customWidthNum > 0 && customWidthNum < 100) {
      // Valor > 100 indica pixel antigo - converter para 100%
      table.style.width = `${customWidthNum}%`;
    } else {
      table.style.width = '100%';
      table.removeAttribute('data-custom-width');
    }

    // Aplicar alinhamento
    switch (tableAlign) {
      case 'left':
        table.style.marginLeft = '0';
        table.style.marginRight = 'auto';
        break;
      case 'center':
        table.style.marginLeft = 'auto';
        table.style.marginRight = 'auto';
        break;
      case 'right':
        table.style.marginLeft = 'auto';
        table.style.marginRight = '0';
        break;
    }

    // Remover handles existentes primeiro
    table.querySelectorAll('.column-resize-handle, .row-resize-handle, .table-resize-handle').forEach(handle => handle.remove());

    // Adicionar handles de colunas em todas as linhas
    const allRows = table.querySelectorAll('tr');

    // Obter numero total de colunas base no width real
    let totalLogicalCols = 0;
    if (allRows.length > 0) {
      const firstRowCells = allRows[0].querySelectorAll('td, th');
      firstRowCells.forEach(cell => {
        totalLogicalCols += parseInt(cell.getAttribute('colspan') || '1', 10);
      });
    }

    allRows.forEach(row => {
      const cells = row.querySelectorAll('td, th');
      let currentLogicalCol = 0;

      cells.forEach((cell) => {
        const colspan = parseInt(cell.getAttribute('colspan') || '1', 10);
        // O índice lógico da borda direita da célula para o qual ela puxa/empurra as margens
        const handleColIndex = currentLogicalCol + colspan - 1;

        // Não adicionar handle na última coluna lógica visível na direita
        if (handleColIndex >= totalLogicalCols - 1) {
          currentLogicalCol += colspan;
          return;
        }

        const handle = document.createElement('div');
        handle.className = 'column-resize-handle';

        // Adicionar event listener para iniciar redimensionamento no local lógico!
        handle.addEventListener('mousedown', (e) => {
          e.preventDefault();
          handle.classList.add('resizing');
          handleColumnResizeStart(e, table, handleColIndex);
        });

        // Garantir que a célula tenha position relative
        const cellElement = cell as HTMLElement;
        cellElement.style.position = 'relative';
        cellElement.appendChild(handle);

        currentLogicalCol += colspan;
      });
    });

    // Adicionar handles de linhas
    const rows = table.querySelectorAll('tr');
    rows.forEach((row, index) => {
      // Não adicionar handle na última linha (ou adicionar, mas o padrão aqui era n-1)
      if (index >= rows.length - 1) return;

      const cells = row.querySelectorAll('td, th');
      if (cells.length === 0) return;

      // Adicionar o handle na primeira célula da linha, que terá position relative 
      // para não quebrar o HTML injetando a <div> diretamente no <tr>
      const targetCell = cells[0] as HTMLElement;
      targetCell.style.position = 'relative';

      const handle = document.createElement('div');
      handle.className = 'row-resize-handle';

      // Adicionar event listener para iniciar redimensionamento
      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        handle.classList.add('resizing');
        handleRowResizeStart(e, table, index);
      });

      targetCell.appendChild(handle);
    });

    // Adicionar handle de redimensionamento da tabela (canto inferior direito)
    const tableHandle = document.createElement('div');
    tableHandle.className = 'table-resize-handle';

    // Adicionar event listener para iniciar redimensionamento da tabela
    tableHandle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      tableHandle.classList.add('resizing');
      handleTableResizeStart(e, table);
    });

    // Inserir o handle da tabela na última célula da última linha para evitar HTML inválido 
    const lastRow = rows[rows.length - 1];
    if (lastRow) {
      const lastRowCells = lastRow.querySelectorAll('td, th');
      if (lastRowCells.length > 0) {
        const lastCell = lastRowCells[lastRowCells.length - 1] as HTMLElement;
        lastCell.style.position = 'relative';
        lastCell.appendChild(tableHandle);
      } else {
        table.appendChild(tableHandle);
      }
    } else {
      table.appendChild(tableHandle);
    }
  };

  const resetTableToFullWidth = (table: HTMLTableElement) => {
    // Remover dimensões customizadas
    table.removeAttribute('data-custom-width');
    table.removeAttribute('data-custom-height');

    // Restaurar configurações padrão
    table.style.width = '100%';
    table.style.height = 'auto';
    table.style.tableLayout = 'fixed';
    table.style.marginLeft = 'auto';
    table.style.marginRight = 'auto';

    // Recriar handles
    addResizeHandles(table);

    // Salvar mudanças
    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML, true);
    }
  };

  const setTableAlignment = (alignment: 'left' | 'center' | 'right') => {
    if (!tableContextMenu) return;

    const { table } = tableContextMenu;

    switch (alignment) {
      case 'left':
        table.style.marginLeft = '0';
        table.style.marginRight = 'auto';
        break;
      case 'center':
        table.style.marginLeft = 'auto';
        table.style.marginRight = 'auto';
        break;
      case 'right':
        table.style.marginLeft = 'auto';
        table.style.marginRight = '0';
        break;
    }

    // Salvar alinhamento como atributo
    table.setAttribute('data-table-align', alignment);

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML, true);
    }
    handleTableContextMenuClose();
  };

  const handleTablePropertiesSave = (properties: TableProperties) => {
    if (!tableContextMenu) return;
    const { table } = tableContextMenu;

    // Aplicar alinhamento via função já existente ou setando direto
    table.setAttribute('data-table-align', properties.alignment);
    switch (properties.alignment) {
      case 'left':
        table.style.marginLeft = '0';
        table.style.marginRight = 'auto';
        break;
      case 'center':
        table.style.marginLeft = 'auto';
        table.style.marginRight = 'auto';
        break;
      case 'right':
        table.style.marginLeft = 'auto';
        table.style.marginRight = '0';
        break;
    }

    // Cor de fundo
    table.style.backgroundColor = properties.backgroundColor;

    // Spacing
    const rawSpacing = properties.cellSpacing.toString().trim() || '0px';
    const cellSpacingStr = /^\d+$/.test(rawSpacing) ? `${rawSpacing}px` : rawSpacing;

    table.style.borderSpacing = cellSpacingStr;
    table.setAttribute('cellspacing', cellSpacingStr.replace('px', ''));

    if (cellSpacingStr !== '0px' && cellSpacingStr !== '0' && cellSpacingStr !== '') {
      table.style.borderCollapse = 'separate';
    } else {
      table.style.borderCollapse = 'collapse';
    }

    // Parse padding
    const rawPadding = properties.cellPadding.toString().trim() || '0px';
    const cellPaddingStr = /^\d+$/.test(rawPadding) ? `${rawPadding}px` : rawPadding;
    table.setAttribute('cellpadding', cellPaddingStr.replace('px', ''));

    // Parse border width
    const rawBorderWidth = properties.borderWidth.toString().trim() || '1px';
    const borderWidthStr = /^\d+$/.test(rawBorderWidth) ? `${rawBorderWidth}px` : rawBorderWidth;

    // Aplicar estilo de borda na tabela em si
    table.style.borderWidth = borderWidthStr;
    table.style.borderStyle = properties.borderStyle;
    table.style.borderColor = properties.borderColor;

    // Repassar para as células
    const cells = table.querySelectorAll('td, th');
    cells.forEach(c => {
      const cell = c as HTMLElement;
      cell.style.padding = cellPaddingStr;
      cell.style.borderWidth = borderWidthStr;
      cell.style.borderStyle = properties.borderStyle;
      cell.style.borderColor = properties.borderColor;
    });

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML, true);
    }
    handleTableContextMenuClose();
  };

  const handleFontSizeChange = (size: number) => {
    // document.execCommand('fontSize') usa escala 1-7, então vamos usar uma abordagem diferente
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      if (!range.collapsed) {
        // Há texto selecionado
        const span = document.createElement('span');
        span.style.fontSize = `${size}px`;

        try {
          range.surroundContents(span);
        } catch (e) {
          // Se não conseguir envolver (seleção complexa), extraímos o conteúdo preservando o HTML interno
          const fragment = range.extractContents();
          span.appendChild(fragment);
          range.insertNode(span);
        }

        // Atualizar estado e conteúdo
        setFontStyle(prev => ({ ...prev, fontSize: size }));
        if (editorRef.current) {
          handleContentChange(editorRef.current.innerHTML, true);
        }

        // Limpar seleção
        selection.removeAllRanges();
      }
    }
  };

  const handleFontFamilyChange = (family: string) => {
    // Usar abordagem similar para garantir consistência
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      if (!range.collapsed) {
        // Há texto selecionado
        const span = document.createElement('span');
        span.style.fontFamily = family;

        try {
          range.surroundContents(span);
        } catch (e) {
          // Se não conseguir envolver (seleção complexa), extraímos o conteúdo preservando o HTML interno
          const fragment = range.extractContents();
          span.appendChild(fragment);
          range.insertNode(span);
        }

        // Atualizar estado e conteúdo
        setFontStyle(prev => ({ ...prev, fontFamily: family }));
        if (editorRef.current) {
          handleContentChange(editorRef.current.innerHTML, true);
        }

        // Limpar seleção
        selection.removeAllRanges();
      }
    }
  };

  const handleParagraphStyleChange = (style: string) => {
    setCurrentParagraphStyle(style);

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      // Encontrar o elemento de bloco atual
      let element: Node | null = range.startContainer;
      if (element.nodeType === Node.TEXT_NODE) {
        element = element.parentNode;
      }

      // Encontrar o elemento de bloco mais próximo
      while (element && element !== editorRef.current && !(element as Element).tagName?.match(/^(P|DIV|H[1-6]|BLOCKQUOTE|PRE)$/)) {
        element = element.parentNode;
      }

      if (element && element !== editorRef.current) {
        const currentElement = element as HTMLElement;
        const content = currentElement.innerHTML;
        const computedStyle = window.getComputedStyle(currentElement);

        // Criar novo elemento com o estilo selecionado
        const newElement = document.createElement(style);
        newElement.innerHTML = content;

        // Preservar alguns estilos importantes
        newElement.style.fontFamily = computedStyle.fontFamily;
        newElement.style.textAlign = computedStyle.textAlign;

        // Aplicar estilos específicos baseados no tipo
        switch (style) {
          case 'blockquote':
            newElement.style.borderLeft = '4px solid #ccc';
            newElement.style.paddingLeft = '16px';
            newElement.style.margin = '16px 0';
            newElement.style.fontStyle = 'italic';
            newElement.style.color = '#666';
            break;
          case 'pre':
            newElement.style.backgroundColor = '#f5f5f5';
            newElement.style.padding = '12px';
            newElement.style.borderRadius = '4px';
            newElement.style.fontFamily = 'Monaco, Menlo, "Ubuntu Mono", monospace';
            newElement.style.fontSize = '13px';
            newElement.style.overflow = 'auto';
            newElement.style.whiteSpace = 'pre-wrap';
            break;
          case 'h1':
            newElement.style.fontSize = '2em';
            newElement.style.fontWeight = 'bold';
            newElement.style.margin = '0.67em 0';
            break;
          case 'h2':
            newElement.style.fontSize = '1.5em';
            newElement.style.fontWeight = 'bold';
            newElement.style.margin = '0.75em 0';
            break;
          case 'h3':
            newElement.style.fontSize = '1.17em';
            newElement.style.fontWeight = 'bold';
            newElement.style.margin = '0.83em 0';
            break;
          case 'h4':
            newElement.style.fontSize = '1em';
            newElement.style.fontWeight = 'bold';
            newElement.style.margin = '1.12em 0';
            break;
          case 'h5':
            newElement.style.fontSize = '0.83em';
            newElement.style.fontWeight = 'bold';
            newElement.style.margin = '1.5em 0';
            break;
          case 'h6':
            newElement.style.fontSize = '0.75em';
            newElement.style.fontWeight = 'bold';
            newElement.style.margin = '1.67em 0';
            break;
          case 'p':
            newElement.style.margin = '1em 0';
            break;
        }

        // Substituir o elemento
        currentElement.parentNode?.replaceChild(newElement, currentElement);

        // Restaurar a seleção
        const newRange = document.createRange();
        newRange.selectNodeContents(newElement);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);

        // Atualizar o conteúdo
        if (editorRef.current) {
          handleContentChange(editorRef.current.innerHTML, true);
        }
      }
    }
  };

  const handleOrderedListClick = (listType?: string) => {
    const typeToUse = listType || currentListType;

    // Se um tipo específico foi passado, atualizar o estado
    if (listType) {
      setCurrentListType(listType);
    }
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      // Encontrar o elemento de bloco atual
      let element: Node | null = range.startContainer;
      if (element.nodeType === Node.TEXT_NODE) {
        element = element.parentNode;
      }

      // Encontrar o elemento de bloco mais próximo
      while (element && element !== editorRef.current && !(element as Element).tagName?.match(/^(P|DIV|H[1-6]|BLOCKQUOTE|PRE|LI)$/)) {
        element = element.parentNode;
      }

      if (element && element !== editorRef.current) {
        const currentElement = element as HTMLElement;

        // Verificar se já está em uma lista
        const parentList = currentElement.closest('ol, ul');

        if (parentList && parentList.tagName === 'OL') {
          // Já está em lista ordenada, remover
          const listItems = Array.from(parentList.children);
          const fragment = document.createDocumentFragment();

          listItems.forEach(li => {
            const p = document.createElement('p');
            p.innerHTML = li.innerHTML;
            p.style.margin = '1em 0';
            fragment.appendChild(p);
          });

          parentList.parentNode?.replaceChild(fragment, parentList);
        } else if (parentList && parentList.tagName === 'UL') {
          // Está em lista não ordenada, converter para ordenada
          const ol = document.createElement('ol');
          ol.style.paddingLeft = '20px';
          ol.style.margin = '1em 0';
          ol.style.listStyleType = typeToUse;

          Array.from(parentList.children).forEach(li => {
            const newLi = document.createElement('li');
            newLi.innerHTML = li.innerHTML;
            newLi.style.marginBottom = '0.5em';
            ol.appendChild(newLi);
          });

          parentList.parentNode?.replaceChild(ol, parentList);
        } else {
          // Não está em lista, criar lista ordenada
          const ol = document.createElement('ol');
          ol.style.paddingLeft = '20px';
          ol.style.margin = '1em 0';
          ol.style.listStyleType = typeToUse;

          const li = document.createElement('li');
          li.innerHTML = currentElement.innerHTML;
          li.style.marginBottom = '0.5em';
          ol.appendChild(li);

          currentElement.parentNode?.replaceChild(ol, currentElement);

          // Restaurar seleção
          const newRange = document.createRange();
          newRange.selectNodeContents(li);
          newRange.collapse(false);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }

        // Atualizar conteúdo
        if (editorRef.current) {
          handleContentChange(editorRef.current.innerHTML, true);
        }
      }
    }
  };

  const handleUnorderedListClick = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      // Encontrar o elemento de bloco atual
      let element: Node | null = range.startContainer;
      if (element.nodeType === Node.TEXT_NODE) {
        element = element.parentNode;
      }

      // Encontrar o elemento de bloco mais próximo
      while (element && element !== editorRef.current && !(element as Element).tagName?.match(/^(P|DIV|H[1-6]|BLOCKQUOTE|PRE|LI)$/)) {
        element = element.parentNode;
      }

      if (element && element !== editorRef.current) {
        const currentElement = element as HTMLElement;

        // Verificar se já está em uma lista
        const parentList = currentElement.closest('ol, ul');

        if (parentList && parentList.tagName === 'UL') {
          // Já está em lista não ordenada, remover
          const listItems = Array.from(parentList.children);
          const fragment = document.createDocumentFragment();

          listItems.forEach(li => {
            const p = document.createElement('p');
            p.innerHTML = li.innerHTML;
            p.style.margin = '1em 0';
            fragment.appendChild(p);
          });

          parentList.parentNode?.replaceChild(fragment, parentList);
        } else if (parentList && parentList.tagName === 'OL') {
          // Está em lista ordenada, converter para não ordenada
          const ul = document.createElement('ul');
          ul.style.paddingLeft = '20px';
          ul.style.margin = '1em 0';

          Array.from(parentList.children).forEach(li => {
            const newLi = document.createElement('li');
            newLi.innerHTML = li.innerHTML;
            newLi.style.marginBottom = '0.5em';
            ul.appendChild(newLi);
          });

          parentList.parentNode?.replaceChild(ul, parentList);
        } else {
          // Não está em lista, criar lista não ordenada
          const ul = document.createElement('ul');
          ul.style.paddingLeft = '20px';
          ul.style.margin = '1em 0';

          const li = document.createElement('li');
          li.innerHTML = currentElement.innerHTML;
          li.style.marginBottom = '0.5em';
          ul.appendChild(li);

          currentElement.parentNode?.replaceChild(ul, currentElement);

          // Restaurar seleção
          const newRange = document.createRange();
          newRange.selectNodeContents(li);
          newRange.collapse(false);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }

        // Atualizar conteúdo
        if (editorRef.current) {
          handleContentChange(editorRef.current.innerHTML, true);
        }
      }
    }
  };

  const insertHtmlAtCursor = (html: string) => {
    console.log('insertHtmlAtCursor chamado com:', html);

    if (disabled) {
      console.log('Editor desabilitado, cancelando inserção');
      return;
    }

    // Focar no editor primeiro
    if (editorRef.current) {
      editorRef.current.focus();
      console.log('Editor focado');
    }

    const selection = window.getSelection();

    // Restaurar a seleção salva antes do modal, se existir
    if (savedRangeRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(savedRangeRef.current);
      savedRangeRef.current = null; // Consumido
    }

    console.log('Selection:', selection);

    if (selection && selection.rangeCount > 0) {
      console.log('Usando inserção com selection');
      const range = selection.getRangeAt(0);

      // Garantir que a seleção atual está DE FATO dentro do editor
      let isInsideEditor = false;
      let node: Node | null = range.commonAncestorContainer;
      while (node) {
        if (node === editorRef.current) {
          isInsideEditor = true;
          break;
        }
        node = node.parentNode;
      }

      if (!isInsideEditor) {
        console.log('Seleção fora do editor. Usando fallback - inserindo no final');
        if (editorRef.current) {
          editorRef.current.innerHTML += html;
          console.log('Conteúdo após fallback:', editorRef.current.innerHTML);
          handleContentChange(editorRef.current.innerHTML);
        }
        return;
      }

      // Deletar conteúdo selecionado se houver
      range.deleteContents();

      // Criar um elemento temporário para converter HTML em nós DOM
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Inserir cada nó do HTML
      const fragment = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
      }

      range.insertNode(fragment);

      // Mover cursor para depois do conteúdo inserido
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);

      // Atualizar conteúdo
      if (editorRef.current) {
        console.log('Conteúdo antes da atualização:', editorRef.current.innerHTML);
        handleContentChange(editorRef.current.innerHTML);
        console.log('Conteúdo após atualização:', editorRef.current.innerHTML);
      }
    } else {
      console.log('Usando fallback - inserindo no final');
      // Fallback: inserir no final do editor
      if (editorRef.current) {
        editorRef.current.innerHTML += html;
        console.log('Conteúdo após fallback:', editorRef.current.innerHTML);
        handleContentChange(editorRef.current.innerHTML);
      }
    }
  };

  const handleImageInsert = (imageUrl: string, altText: string) => {
    console.log('Inserindo imagem:', { imageUrl, altText });
    const img = `<img src="${imageUrl}" alt="${altText}" style="max-width: 100%; height: auto; margin: 5px 0;" />`;
    insertHtmlAtCursor(img);
    setIsImageModalOpen(false);
  };

  const handleTableInsert = (tableHtml: string) => {
    console.log('Inserindo tabela:', tableHtml);
    insertHtmlAtCursor(tableHtml);
    setIsTableModalOpen(false);

    // Adicionar handles de redimensionamento à nova tabela após um pequeno delay
    setTimeout(() => {
      if (editorRef.current) {
        const tables = editorRef.current.querySelectorAll('table');
        tables.forEach(table => {
          const tableElement = table as HTMLTableElement;
          // Definir alinhamento padrão como centralizado
          tableElement.setAttribute('data-table-align', 'center');
          addResizeHandles(tableElement);
        });
      }
    }, 100);
  };

  const handlePageBreakInsert = () => {
    const pageBreakHtml = `
      <div class="page-break" style="
        page-break-before: always;
        break-before: page;
        height: 1px;
        border-top: 2px dashed #ccc;
        margin: 20px 0;
        position: relative;
        background: transparent;
        display: block;
        width: 100%;
      " contenteditable="false">
        <span style="
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          padding: 0 10px;
          font-size: 12px;
          color: #666;
          font-family: Arial, sans-serif;
          user-select: none;
        ">Quebra de Página</span>
      </div>
    `;

    insertHtmlAtCursor(pageBreakHtml);
    const newContent = editorRef.current?.innerHTML || '';
    handleContentChange(newContent, true);
  };

  const handleEmojiInsert = (emoji: string) => {
    insertHtmlAtCursor(emoji);
    const newContent = editorRef.current?.innerHTML || '';
    handleContentChange(newContent, true);
  };

  const handleImageDoubleClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'IMG') {
      setSelectedImage(target as HTMLImageElement);
      setIsImageResizeModalOpen(true);
    }
  };

  const handleImageResize = (width: number, height: number, maintainAspect: boolean) => {
    if (selectedImage) {
      selectedImage.style.width = `${width}px`;
      if (!maintainAspect) {
        selectedImage.style.height = `${height}px`;
      } else {
        selectedImage.style.height = 'auto';
      }

      // Atualizar conteúdo do editor
      if (editorRef.current) {
        handleContentChange(editorRef.current.innerHTML);
      }
    }
    setSelectedImage(null);
  };

  // Função para detectar clique direito em tabelas
  const handleTableRightClick = (event: Event) => {
    const mouseEvent = event as MouseEvent;
    const target = mouseEvent.target as HTMLElement;

    // Verificar se o clique foi em uma célula de tabela
    const cell = target.closest('td, th') as HTMLTableCellElement;
    const table = target.closest('table') as HTMLTableElement;

    if (cell && table) {
      mouseEvent.preventDefault();

      // Encontrar posição da célula
      const row = Array.from(table.rows).findIndex(r =>
        Array.from(r.cells).includes(cell)
      );
      const col = Array.from(table.rows[row].cells).indexOf(cell);

      setTableContextMenu({
        mouseX: mouseEvent.clientX - 2,
        mouseY: mouseEvent.clientY - 4,
        table,
        cell,
        row,
        col
      });
    }
  };

  const handleTableContextMenuClose = () => {
    setTableContextMenu(null);
    setHoveredSubmenu(null);
    setSubmenuAnchor({ element: null, type: '' });
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
      submenuTimeoutRef.current = null;
    }
  };

  const handleSubmenuEnter = (type: string, element: HTMLElement) => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
      submenuTimeoutRef.current = null;
    }
    setHoveredSubmenu(type);
    setSubmenuAnchor({ element, type });
  };

  const handleSubmenuLeave = () => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
    submenuTimeoutRef.current = setTimeout(() => {
      setHoveredSubmenu(null);
      setSubmenuAnchor({ element: null, type: '' });
    }, 150); // 150ms de delay para permitir movimento do mouse
  };

  const handleSubmenuStay = (type: string) => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
      submenuTimeoutRef.current = null;
    }
    setHoveredSubmenu(type);
  };

  // Funções de manipulação de tabela
  const insertTableRowAbove = () => {
    if (!tableContextMenu) return;

    const { table, row } = tableContextMenu;
    const newRow = table.insertRow(row);

    // Adicionar células com base na primeira linha
    const cellCount = table.rows[0].cells.length;
    for (let i = 0; i < cellCount; i++) {
      const newCell = newRow.insertCell();
      newCell.innerHTML = '&nbsp;';
      newCell.style.border = '1px solid #ddd';
      newCell.style.padding = '0';
      newCell.style.fontSize = '12px';
    }

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const insertTableRowBelow = () => {
    if (!tableContextMenu) return;

    const { table, row } = tableContextMenu;
    const newRow = table.insertRow(row + 1);

    // Adicionar células com base na primeira linha
    const cellCount = table.rows[0].cells.length;
    for (let i = 0; i < cellCount; i++) {
      const newCell = newRow.insertCell();
      newCell.innerHTML = '&nbsp;';
      newCell.style.border = '1px solid #ddd';
      newCell.style.padding = '0';
      newCell.style.fontSize = '12px';
    }

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const insertTableColumnLeft = () => {
    if (!tableContextMenu) return;

    const { table, col } = tableContextMenu;

    // Adicionar célula em cada linha
    Array.from(table.rows).forEach(row => {
      const newCell = row.insertCell(col);
      newCell.innerHTML = '&nbsp;';
      newCell.style.border = '1px solid #ddd';
      newCell.style.padding = '0';
      newCell.style.fontSize = '12px';
    });

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }

    // Recriar handles de redimensionamento
    setTimeout(() => {
      addResizeHandles(table);
    }, 50);

    handleTableContextMenuClose();
  };

  const insertTableColumnRight = () => {
    if (!tableContextMenu) return;

    const { table, col } = tableContextMenu;

    // Adicionar célula em cada linha
    Array.from(table.rows).forEach(row => {
      const newCell = row.insertCell(col + 1);
      newCell.innerHTML = '&nbsp;';
      newCell.style.border = '1px solid #ddd';
      newCell.style.padding = '0';
      newCell.style.fontSize = '12px';
    });

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }

    // Recriar handles de redimensionamento
    setTimeout(() => {
      addResizeHandles(table);
    }, 50);

    handleTableContextMenuClose();
  };

  const deleteTableRow = () => {
    if (!tableContextMenu) return;

    const { table, row } = tableContextMenu;

    // Não permitir deletar se só há uma linha
    if (table.rows.length <= 1) return;

    table.deleteRow(row);

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const deleteTableColumn = () => {
    if (!tableContextMenu) return;

    const { table, col } = tableContextMenu;

    // Não permitir deletar se só há uma coluna
    if (table.rows[0].cells.length <= 1) return;

    // Deletar célula de cada linha
    Array.from(table.rows).forEach(row => {
      if (row.cells[col]) {
        row.deleteCell(col);
      }
    });

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const deleteTable = () => {
    if (!tableContextMenu) return;

    const { table } = tableContextMenu;
    table.remove();

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const mergeTableCellRight = () => {
    if (!tableContextMenu) return;

    const { table, cell, row, col } = tableContextMenu;
    const nextCell = table.rows[row].cells[col + 1];

    if (!nextCell) return;

    // Mesclar conteúdo
    if (nextCell.innerHTML !== '&nbsp;' && cell.innerHTML !== '&nbsp;') {
      cell.innerHTML += ' ' + nextCell.innerHTML;
    } else if (nextCell.innerHTML !== '&nbsp;') {
      cell.innerHTML = nextCell.innerHTML;
    }

    // Aumentar colspan
    const currentColspan = parseInt(cell.getAttribute('colspan') || '1');
    const nextColspan = parseInt(nextCell.getAttribute('colspan') || '1');
    cell.setAttribute('colspan', (currentColspan + nextColspan).toString());

    // Remover célula mesclada
    nextCell.remove();

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const mergeTableCellDown = () => {
    if (!tableContextMenu) return;

    const { table, cell, row, col } = tableContextMenu;
    const belowRow = table.rows[row + 1];

    if (!belowRow) return;

    const belowCell = belowRow.cells[col];
    if (!belowCell) return;

    // Mesclar conteúdo
    if (belowCell.innerHTML !== '&nbsp;' && cell.innerHTML !== '&nbsp;') {
      cell.innerHTML += ' ' + belowCell.innerHTML;
    } else if (belowCell.innerHTML !== '&nbsp;') {
      cell.innerHTML = belowCell.innerHTML;
    }

    // Aumentar rowspan
    const currentRowspan = parseInt(cell.getAttribute('rowspan') || '1');
    const belowRowspan = parseInt(belowCell.getAttribute('rowspan') || '1');
    cell.setAttribute('rowspan', (currentRowspan + belowRowspan).toString());

    // Remover célula mesclada
    belowCell.remove();

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const mergeSelectedCells = () => {
    if (!tableContextMenu) return;

    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLTableCellElement[];
    if (cells.length < 2) return;

    const targetCell = cells[0];

    // Obter todas as linhas envolvidas
    const rowElements = Array.from(new Set(cells.map(c => c.parentElement)));

    // Calcular o colspan total iterando nas células da primeira linha
    const firstRowCells = cells.filter(c => c.parentElement === rowElements[0]);
    const totalColspan = firstRowCells.reduce((sum, c) => sum + parseInt(c.getAttribute('colspan') || '1'), 0);

    // Calcular o rowspan total iterando na primeira célula de cada linha da seleção
    const firstColCells = rowElements.map(row => cells.find(c => c.parentElement === row)).filter(Boolean) as HTMLTableCellElement[];
    const totalRowspan = firstColCells.reduce((sum, c) => sum + parseInt(c.getAttribute('rowspan') || '1'), 0);

    let combinedHtml = '';

    cells.forEach((cell, index) => {
      // Concatenar conteúdo se houver
      if (cell.innerHTML !== '&nbsp;' && cell.innerHTML.trim() !== '') {
        combinedHtml += (combinedHtml && cell.innerHTML !== '&nbsp;') ? ' ' + cell.innerHTML : cell.innerHTML;
      }

      // Remover todas exceto a primeira
      if (index > 0) {
        cell.remove();
      }
    });

    // Limpar o status de seleção da célula que restou
    targetCell.classList.remove('syseditor-selected-cell');

    targetCell.innerHTML = combinedHtml || '&nbsp;';
    targetCell.setAttribute('colspan', totalColspan.toString());
    targetCell.setAttribute('rowspan', totalRowspan.toString());

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const setTableCellAlignment = (alignment: string) => {
    if (!tableContextMenu) return;

    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];
    targetCells.forEach(cell => {
      cell.style.textAlign = alignment;
    });

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const setTableCellBackgroundColor = (color: string) => {
    if (!tableContextMenu) return;

    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];
    targetCells.forEach(cell => {
      cell.style.backgroundColor = color;
    });

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };


  // Funções para bordas de célula individual
  const setCellBorderStyle = (borderStyle: string) => {
    if (!tableContextMenu) return;

    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];

    targetCells.forEach(cell => {
      cell.style.borderStyle = borderStyle;

      // Garantir que há uma largura mínima se não estiver definida
      if (!cell.style.borderWidth) {
        cell.style.borderWidth = '1px';
      }
      // Garantir que há uma cor se não estiver definida
      if (!cell.style.borderColor) {
        cell.style.borderColor = '#000000';
      }
    });

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const setCellBorderWidth = (borderWidth: string) => {
    if (!tableContextMenu) return;

    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];

    targetCells.forEach(cell => {
      cell.style.borderWidth = borderWidth;

      // Garantir que há um estilo se não estiver definido
      if (!cell.style.borderStyle || cell.style.borderStyle === 'none') {
        cell.style.borderStyle = 'solid';
      }
      // Garantir que há uma cor se não estiver definida
      if (!cell.style.borderColor) {
        cell.style.borderColor = '#000000';
      }
    });

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const setCellBorderColor = (borderColor: string) => {
    if (!tableContextMenu) return;

    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];

    targetCells.forEach(cell => {
      cell.style.borderColor = borderColor;

      // Garantir que há uma largura se não estiver definida
      if (!cell.style.borderWidth) {
        cell.style.borderWidth = '1px';
      }
      // Garantir que há um estilo se não estiver definido
      if (!cell.style.borderStyle || cell.style.borderStyle === 'none') {
        cell.style.borderStyle = 'solid';
      }
    });

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const removeCellBorders = () => {
    if (!tableContextMenu) return;

    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];

    targetCells.forEach(cell => {
      cell.style.border = 'none';
      cell.style.borderTop = 'none';
      cell.style.borderRight = 'none';
      cell.style.borderBottom = 'none';
      cell.style.borderLeft = 'none';
    });

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  const copyTableCell = () => {
    if (!tableContextMenu) return;

    const { cell } = tableContextMenu;
    setCopiedTableCell({
      content: cell.innerHTML,
      backgroundColor: cell.style.backgroundColor,
      textAlign: cell.style.textAlign
    });
    handleTableContextMenuClose();
  };

  const pasteTableCell = () => {
    if (!tableContextMenu || !copiedTableCell) return;

    const { cell } = tableContextMenu;
    cell.innerHTML = copiedTableCell.content;
    if (copiedTableCell.backgroundColor) {
      cell.style.backgroundColor = copiedTableCell.backgroundColor;
    }
    if (copiedTableCell.textAlign) {
      cell.style.textAlign = copiedTableCell.textAlign;
    }

    if (editorRef.current) {
      handleContentChange(editorRef.current.innerHTML);
    }
    handleTableContextMenuClose();
  };

  // Funções de desfazer/refazer
  const handleUndo = () => {
    if (historyIndex > 0) {
      // Limpar timer se estiver ativo
      if (typingTimer) {
        clearTimeout(typingTimer);
        setTypingTimer(null);
      }

      const newIndex = historyIndex - 1;
      const previousContent = history[newIndex];

      setHistoryIndex(newIndex);
      setEditorState(prev => ({ ...prev, content: previousContent }));
      setLastSavedContent(previousContent);

      if (editorRef.current) {
        editorRef.current.innerHTML = previousContent;
      }

      onChange?.(previousContent);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      // Limpar timer se estiver ativo
      if (typingTimer) {
        clearTimeout(typingTimer);
        setTypingTimer(null);
      }

      const newIndex = historyIndex + 1;
      const nextContent = history[newIndex];

      setHistoryIndex(newIndex);
      setEditorState(prev => ({ ...prev, content: nextContent }));
      setLastSavedContent(nextContent);

      if (editorRef.current) {
        editorRef.current.innerHTML = nextContent;
      }

      onChange?.(nextContent);
    }
  };

  // Funções de OpenAI
  const handleAiModalOpen = () => {
    setIsAiModalOpen(true);
    setAiPrompt('');
    setAiError(null);
  };

  const handleAiModalClose = () => {
    setIsAiModalOpen(false);
    setAiPrompt('');
    setAiResponse('');
    setAiError(null);
    setIsAiLoading(false);
  };

  const handleInsertAiResponse = () => {
    if (aiResponse.trim()) {
      // Inserir o texto da resposta no editor
      insertHtmlAtCursor(`<p>${aiResponse}</p>`);
      handleAiModalClose();
    }
  };



  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      setAiError('Por favor, digite um prompt para gerar o texto.');
      return;
    }

    setIsAiLoading(true);
    setAiError(null);

    try {
      // Obter token de autenticação (usando a chave correta do sistema)
      const token = localStorage.getItem('sysup_token');

      if (!token) {
        setAiError('Você precisa estar logado para usar a IA.');
        setIsAiLoading(false);
        return;
      }

      console.log('🤖 Gerando texto com IA...');
      console.log('📝 Prompt:', aiPrompt);

      // Chamada para a API do backend usando a URL base correta
      const response = await fetch(`${env.API_URL}/api/openai/generate-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: aiPrompt,
          maxTokens: 500,
          temperature: 0.7
        }),
      });

      console.log('📡 Status da resposta:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Erro da API:', errorData);

        if (response.status === 401) {
          setAiError('Sessão expirada. Faça login novamente.');
        } else if (errorData.code === 'OPENAI_NOT_CONFIGURED') {
          setAiError('Serviço de IA não configurado. Entre em contato com o administrador para configurar o token da OpenAI.');
        } else {
          setAiError(errorData.error || 'Erro ao gerar texto com IA');
        }
        return;
      }

      const data = await response.json();
      console.log('✅ Resposta da IA:', data);

      const generatedText = data.text || '';

      if (generatedText) {
        // Colocar o texto gerado no campo de resposta
        setAiResponse(generatedText);

        // Mostrar aviso se estiver usando fallback
        if (data.source === 'fallback') {
          console.warn('⚠️ Usando resposta simulada:', data.warning);
        } else {
          console.log('🎉 Texto gerado com sucesso pela OpenAI!');
        }
      } else {
        setAiError('Nenhum texto foi gerado. Tente novamente.');
      }

    } catch (error) {
      console.error('❌ Erro ao gerar texto:', error);
      setAiError('Erro inesperado. Verifique sua conexão e tente novamente.');
    } finally {
      setIsAiLoading(false);
    }
  };

  // Funções de Drag and Drop para variáveis
  const handleDragStart = (e: React.DragEvent, variable: string) => {
    setDraggedVariable(variable);
    e.dataTransfer.setData('text/plain', variable);
    e.dataTransfer.effectAllowed = 'copy';

    // Criar uma imagem de preview personalizada
    const dragImage = document.createElement('div');
    dragImage.innerHTML = variable;
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    dragImage.style.background = '#1976d2';
    dragImage.style.color = 'white';
    dragImage.style.padding = '4px 8px';
    dragImage.style.borderRadius = '4px';
    dragImage.style.fontSize = '12px';
    dragImage.style.fontFamily = 'monospace';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);

    // Remover o elemento após um tempo
    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  };

  const handleDragEnd = () => {
    setDraggedVariable(null);
    setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (draggedVariable) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Só remove o highlight se realmente saiu da área do editor
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const variable = e.dataTransfer.getData('text/plain');
    if (variable) {
      insertVariableAtCursor(variable);
    }
  };

  const insertVariableAtCursor = (variable: string) => {
    insertHtmlAtCursor(variable);
  };

  // Função para obter cor baseada no prefixo da variável
  const getVariableColor = (variable: string): { bg: string; border: string; hover: string } => {
    const prefix = variable.split('.')[0].replace(/[\{\}]/g, '');

    const colorMap: { [key: string]: { bg: string; border: string; hover: string } } = {
      'cliente': { bg: '#e1f5fe', border: '#b3e5fc', hover: '#81d4fa' },        // Azul céu suave
      'contrato': { bg: '#f3e5f5', border: '#e1bee7', hover: '#ce93d8' },       // Lavanda suave
      'plano': { bg: '#e0f2f1', border: '#b2dfdb', hover: '#80cbc4' },          // Verde menta
      'fatura': { bg: '#fff3e0', border: '#ffccbc', hover: '#ffab91' },         // Coral suave
      'provedor': { bg: '#e8f5e9', border: '#c8e6c9', hover: '#a5d6a7' },       // Verde claro suave
      'data': { bg: '#fce4ec', border: '#f8bbd0', hover: '#f48fb1' },           // Rosa salmão
      'sistema': { bg: '#eceff1', border: '#cfd8dc', hover: '#b0bec5' },        // Cinza azulado
    };

    return colorMap[prefix] || { bg: '#fafafa', border: '#e0e0e0', hover: '#bdbdbd' }; // Cinza muito claro
  };

  // Atalhos de teclado
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey) {
      switch (event.key.toLowerCase()) {
        case 'z':
          if (event.shiftKey) {
            event.preventDefault();
            handleRedo();
          } else {
            event.preventDefault();
            handleUndo();
          }
          break;
        case 'y':
          event.preventDefault();
          handleRedo();
          break;
        case 'e':
          event.preventDefault();
          applyFormat('justifyCenter');
          break;
        case 's':
          if (onSave) {
            event.preventDefault();

            // Mostrar feedback visual
            setSavePressed(true);
            setTimeout(() => setSavePressed(false), 1000);

            onSave(editorState.content);
          }
          break;
      }
    } else if (event.key === 'Enter') {
      // Verificar se estamos em uma lista
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let element = range.startContainer;

        if (element.nodeType === Node.TEXT_NODE && element.parentNode) {
          element = element.parentNode;
        }

        // Encontrar se estamos em um item de lista
        const listItem = (element as Element)?.closest('li');
        const list = (element as Element)?.closest('ol, ul');

        if (listItem && list) {
          event.preventDefault();

          // Verificar se o item atual está vazio
          if (listItem.textContent?.trim() === '') {
            // Item vazio, sair da lista
            const p = document.createElement('p');
            p.innerHTML = '&nbsp;';
            p.style.margin = '1em 0';

            if (list.parentNode) {
              list.parentNode.insertBefore(p, list.nextSibling);
            }
            listItem.remove();

            // Se a lista ficou vazia, remover
            if (list.children.length === 0) {
              list.remove();
            }

            // Focar no novo parágrafo
            const newRange = document.createRange();
            newRange.selectNodeContents(p);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
          } else {
            // Criar novo item de lista
            const newLi = document.createElement('li');
            newLi.innerHTML = '&nbsp;';
            newLi.style.marginBottom = '0.5em';

            // Inserir após o item atual
            if (listItem.parentNode) {
              listItem.parentNode.insertBefore(newLi, listItem.nextSibling);
            }

            // Se for lista ordenada, garantir que o tipo está correto
            if (list.tagName === 'OL') {
              (list as HTMLOListElement).style.listStyleType = currentListType;
            }

            // Focar no novo item
            const newRange = document.createRange();
            newRange.selectNodeContents(newLi);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }

          // Atualizar conteúdo
          if (editorRef.current) {
            handleContentChange(editorRef.current.innerHTML, true);
          }
        }
      }
    }
  };

  // Cleanup do timer quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (typingTimer) {
        clearTimeout(typingTimer);
      }
    };
  }, [typingTimer]);

  // Event listeners para redimensionamento de colunas
  useEffect(() => {
    if (isResizingColumn) {
      document.addEventListener('mousemove', handleColumnResizeMove);
      document.addEventListener('mouseup', handleColumnResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleColumnResizeMove);
        document.removeEventListener('mouseup', handleColumnResizeEnd);
      };
    }
  }, [isResizingColumn, resizingColumnData]);

  // Event listeners para redimensionamento de linhas
  useEffect(() => {
    if (isResizingRow) {
      document.addEventListener('mousemove', handleRowResizeMove);
      document.addEventListener('mouseup', handleRowResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleRowResizeMove);
        document.removeEventListener('mouseup', handleRowResizeEnd);
      };
    }
  }, [isResizingRow, resizingRowData]);

  // Event listeners para redimensionamento da tabela
  useEffect(() => {
    if (isResizingTable) {
      document.addEventListener('mousemove', handleTableResizeMove);
      document.addEventListener('mouseup', handleTableResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleTableResizeMove);
        document.removeEventListener('mouseup', handleTableResizeEnd);
      };
    }
  }, [isResizingTable, resizingTableData]);

  // Drag & drop de imagens: permite reposicionar sem inserir base64
  const draggedImgRef = useRef<HTMLImageElement | null>(null);

  const handleImgDragStart = (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      draggedImgRef.current = target as HTMLImageElement;
    }
  };

  const handleImgDragEnd = () => {
    draggedImgRef.current = null;
  };

  const handleEditorDrop = (e: DragEvent) => {
    if (draggedImgRef.current) {
      e.preventDefault();
      e.stopPropagation();

      const img = draggedImgRef.current;
      draggedImgRef.current = null;

      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (!range) return;

      img.parentNode?.removeChild(img);
      range.insertNode(img);

      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        const newRange = document.createRange();
        newRange.setStartAfter(img);
        newRange.collapse(true);
        sel.addRange(newRange);
      }

      if (editorRef.current) {
        handleContentChange(editorRef.current.innerHTML, true);
      }
      return;
    }

    const html = e.dataTransfer?.getData('text/html') || '';
    const text = e.dataTransfer?.getData('text/plain') || '';
    if (html.includes('data:image') || text.startsWith('data:image')) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Adicionar event listeners
  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener('dblclick', handleImageDoubleClick);
      editor.addEventListener('contextmenu', handleTableRightClick);
      editor.addEventListener('keydown', handleKeyDown);
      editor.addEventListener('dragstart', handleImgDragStart);
      editor.addEventListener('dragend', handleImgDragEnd);
      editor.addEventListener('drop', handleEditorDrop);

      return () => {
        editor.removeEventListener('dblclick', handleImageDoubleClick);
        editor.removeEventListener('contextmenu', handleTableRightClick);
        editor.removeEventListener('keydown', handleKeyDown);
        editor.removeEventListener('dragstart', handleImgDragStart);
        editor.removeEventListener('dragend', handleImgDragEnd);
        editor.removeEventListener('drop', handleEditorDrop);
      };
    }
  }, [historyIndex, history]);

  // Adicionar handles de redimensionamento às tabelas existentes
  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      const tables = editor.querySelectorAll('table');
      tables.forEach(table => {
        addResizeHandles(table as HTMLTableElement);
      });
    }
  }, [editorState.content]);

  // Event listeners para seleção de múltiplas células em tabelas
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    let isSelectingCells = false;
    let startCell: HTMLTableCellElement | null = null;
    let currentTable: HTMLTableElement | null = null;

    const clearSelection = (table: HTMLTableElement | null = null) => {
      const container = table || editor;
      container.querySelectorAll('.syseditor-selected-cell').forEach(c => {
        c.classList.remove('syseditor-selected-cell');
      });
    };

    const getCellPos = (cell: HTMLTableCellElement, table: HTMLTableElement) => {
      const row = Array.from(table.rows).findIndex(r => Array.from(r.cells).includes(cell));
      if (row === -1) return { row: -1, col: -1 };
      const col = Array.from(table.rows[row].cells).indexOf(cell);
      return { row, col };
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Ignorar clique direito ou ctrl+click (mac trackpads) para proteger a seleção
      if (e.button !== 0 || e.ctrlKey) return;

      const target = e.target as HTMLElement;
      const cell = target.closest('td, th') as HTMLTableCellElement;

      // Limpar seleção anterior se não clicar numa célula
      if (!cell) {
        clearSelection();
        return;
      }

      // Ignorar controles de redimensionamento
      if (target.classList.contains('column-resize-handle') ||
        target.classList.contains('row-resize-handle') ||
        target.classList.contains('table-resize-handle')) {
        return;
      }

      isSelectingCells = true;
      startCell = cell;
      currentTable = cell.closest('table') as HTMLTableElement;

      // Apenas limpar a seleção anterior se a pessoa clicar fora do quadrante já selecionado
      // Isso impede que possíveis cliques falsos (ou trackpad) zerem o box todo
      if (!cell.classList.contains('syseditor-selected-cell')) {
        clearSelection();
        cell.classList.add('syseditor-selected-cell');
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isSelectingCells || !currentTable || !startCell) return;

      const target = e.target as HTMLElement;
      const cell = target.closest('td, th') as HTMLTableCellElement;

      if (!cell) return;
      if (cell.closest('table') !== currentTable) return;

      // Evitar que o browser selecione textos quebrando o visual
      if (cell !== startCell) {
        window.getSelection()?.removeAllRanges();
      }

      const startPos = getCellPos(startCell, currentTable);
      const currentPos = getCellPos(cell, currentTable);

      if (startPos.row === -1 || currentPos.row === -1) return;

      const minRow = Math.min(startPos.row, currentPos.row);
      const maxRow = Math.max(startPos.row, currentPos.row);
      const minCol = Math.min(startPos.col, currentPos.col);
      const maxCol = Math.max(startPos.col, currentPos.col);

      clearSelection(currentTable);

      for (let r = minRow; r <= maxRow; r++) {
        const rowObj = currentTable.rows[r];
        if (!rowObj) continue;
        for (let c = minCol; c <= maxCol; c++) {
          const colObj = rowObj.cells[c];
          if (colObj) {
            colObj.classList.add('syseditor-selected-cell');
          }
        }
      }
    };

    const handleMouseUp = () => {
      isSelectingCells = false;
    };

    editor.addEventListener('mousedown', handleMouseDown);
    editor.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      editor.removeEventListener('mousedown', handleMouseDown);
      editor.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Funções para bordas individuais das células
  const setCellBorderLeft = (style: string, width: string, color: string) => {
    if (!tableContextMenu) return;
    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];
    targetCells.forEach(cell => {
      cell.style.borderLeftStyle = style;
      cell.style.borderLeftWidth = width;
      cell.style.borderLeftColor = color;
    });
    if (editorRef.current) handleContentChange(editorRef.current.innerHTML);
  };

  const setCellBorderRight = (style: string, width: string, color: string) => {
    if (!tableContextMenu) return;
    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];
    targetCells.forEach(cell => {
      cell.style.borderRightStyle = style;
      cell.style.borderRightWidth = width;
      cell.style.borderRightColor = color;
    });
    if (editorRef.current) handleContentChange(editorRef.current.innerHTML);
  };

  const setCellBorderTop = (style: string, width: string, color: string) => {
    if (!tableContextMenu) return;
    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];
    targetCells.forEach(cell => {
      cell.style.borderTopStyle = style;
      cell.style.borderTopWidth = width;
      cell.style.borderTopColor = color;
    });
    if (editorRef.current) handleContentChange(editorRef.current.innerHTML);
  };

  const setCellBorderBottom = (style: string, width: string, color: string) => {
    if (!tableContextMenu) return;
    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];
    targetCells.forEach(cell => {
      cell.style.borderBottomStyle = style;
      cell.style.borderBottomWidth = width;
      cell.style.borderBottomColor = color;
    });
    if (editorRef.current) handleContentChange(editorRef.current.innerHTML);
  };

  const removeCellBorderLeft = () => {
    if (!tableContextMenu) return;
    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];
    targetCells.forEach(cell => cell.style.borderLeft = 'none');
    if (editorRef.current) handleContentChange(editorRef.current.innerHTML);
  };

  const removeCellBorderRight = () => {
    if (!tableContextMenu) return;
    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];
    targetCells.forEach(cell => cell.style.borderRight = 'none');
    if (editorRef.current) handleContentChange(editorRef.current.innerHTML);
  };

  const removeCellBorderTop = () => {
    if (!tableContextMenu) return;
    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];
    targetCells.forEach(cell => cell.style.borderTop = 'none');
    if (editorRef.current) handleContentChange(editorRef.current.innerHTML);
  };

  const removeCellBorderBottom = () => {
    if (!tableContextMenu) return;
    const cells = Array.from(tableContextMenu.table.querySelectorAll('.syseditor-selected-cell')) as HTMLElement[];
    const targetCells = cells.length > 0 ? cells : [tableContextMenu.cell];
    targetCells.forEach(cell => cell.style.borderBottom = 'none');
    if (editorRef.current) handleContentChange(editorRef.current.innerHTML);
  };

  const handlePrint = () => {
    if (editorRef.current) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        let printWidth = '100%';
        let printMargin = '20px';
        let pageCss = '@page { margin: 10mm; }';
        
        switch (paperSize) {
          case 'a4':
            printWidth = '210mm';
            printMargin = '0 auto';
            pageCss = '@page { size: A4; margin: 10mm; }';
            break;
          case 'a5':
            printWidth = '148mm';
            printMargin = '0 auto';
            pageCss = '@page { size: A5; margin: 10mm; }';
            break;
          case 'cupom80':
            printWidth = '75mm';
            printMargin = '0';
            pageCss = '@page { size: 80mm auto; margin: 0; } body { padding: 0 !important; margin: 0; width: 75mm; overflow: hidden; font-size: 12px; word-break: break-all; word-wrap: break-word; } .editor-content { padding: 0 !important; margin: 0 !important; word-break: break-all; }';
            break;
          case 'cupom57':
            printWidth = '52mm';
            printMargin = '0';
            pageCss = '@page { size: 57mm auto; margin: 0; } body { padding: 0 !important; margin: 0; width: 52mm; overflow: hidden; font-size: 12px; word-break: break-all; word-wrap: break-word; } .editor-content { padding: 0 !important; margin: 0 !important; word-break: break-all; }';
            break;
          default:
            printWidth = '100%';
        }

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Imprimir Documento</title>
              <style>
                body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: ${printMargin}; width: ${printWidth}; box-sizing: border-box; }
                img { max-width: 100%; height: auto; }
                table { border-collapse: collapse; width: 100% !important; table-layout: fixed !important; }
                td, th { word-wrap: break-word !important; word-break: break-all !important; overflow-wrap: break-word !important; border: 1px solid #ddd; padding: 8px; }
                .page-break { page-break-before: always !important; break-before: page !important; border: none !important; height: 0 !important; margin: 0 !important; }
                .page-break span { display: none !important; }
                @media print {
                  body { margin: 0; }
                  ${pageCss}
                }
                ${paperSize === 'a4' || paperSize === 'a5' || paperSize === 'auto' ? 'body { padding: 20px; }' : ''}
              </style>
            </head>
            <body>
              <div class="editor-content">
                ${editorState.content}
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        
        // Aguarda um pequeno delay para garantir o carregamento das imagens antes de imprimir
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    }
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        flex: height ? undefined : 1,
        minHeight: 0,
        ...(isFullscreen && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          m: 0,
          borderRadius: 0,
          height: '100vh',
          bgcolor: 'background.paper'
        })
      }}
    >
      <EditorToolbar
        fontStyle={fontStyle}
        onBoldClick={() => applyFormat('bold')}
        onItalicClick={() => applyFormat('italic')}
        onAlignLeft={() => applyFormat('justifyLeft')}
        onAlignCenter={() => applyFormat('justifyCenter')}
        onAlignRight={() => applyFormat('justifyRight')}
        onAlignJustify={() => applyFormat('justifyFull')}
        onFontSizeChange={handleFontSizeChange}
        onFontFamilyChange={handleFontFamilyChange}
        onParagraphStyleChange={handleParagraphStyleChange}
        onOrderedListClick={handleOrderedListClick}
        onUnorderedListClick={handleUnorderedListClick}
        onImageClick={() => {
          saveCurrentRange();
          setIsImageModalOpen(true);
        }}
        onTableClick={() => {
          saveCurrentRange();
          setIsTableModalOpen(true);
        }}
        onPageBreakClick={handlePageBreakInsert}
        onUndoClick={handleUndo}
        onRedoClick={handleRedo}
        onAiClick={handleAiModalOpen}
        onHelpClick={() => setIsHelpModalOpen(true)}
        onSaveClick={onSave ? () => onSave(editorState.content) : undefined}
        onPrintClick={handlePrint}
        onEmojiClick={handleEmojiInsert}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        currentParagraphStyle={currentParagraphStyle}
        currentListType={currentListType}
        disabled={disabled}
        showSave={!!onSave}
        onUnderlineClick={() => applyFormat('underline')}
        onStrikethroughClick={() => applyFormat('strikeThrough')}
        onSubscriptClick={() => applyFormat('subscript')}
        onSuperscriptClick={() => applyFormat('superscript')}
        onFontColorChange={(color) => applyFormat('foreColor', color)}
        onBackgroundColorChange={(color) => applyFormat('hiliteColor', color)}
        onFullscreenClick={() => setIsFullscreen(!isFullscreen)}
        onSourceCodeClick={() => setIsSourceCode(!isSourceCode)}
        isFullscreen={isFullscreen}
        isSourceCode={isSourceCode}
        paperSize={paperSize}
        onPaperSizeChange={setPaperSize}
      />

      <Divider />

      <Box
        sx={{
          border: isDragOver ? '2px solid' : 'none',
          borderColor: isDragOver ? 'primary.main' : 'transparent',
          borderRadius: 1,
          position: 'relative',
          transition: 'border-color 0.2s ease',
          backgroundColor: paperSize !== 'auto' ? '#f5f5f5' : 'transparent',
          ...(isFullscreen && {
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            alignItems: paperSize !== 'auto' ? 'center' : 'stretch',
            py: paperSize !== 'auto' ? 4 : 0,
            height: 'calc(100vh - 65px)' // Desconta aproximado a toolbar
          }),
          ...(!isFullscreen && paperSize !== 'auto' && {
            display: 'flex',
            justifyContent: 'center',
            py: 2,
            overflow: 'visible'
          }),
          ...(!isFullscreen && paperSize === 'auto' && {
            overflow: 'auto',
            minHeight: height ? height : 0,
            flex: height ? undefined : 1,
            display: 'flex',
            flexDirection: 'column'
          })
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
            {/* Container do Editor (Simula Papel) */}
        <Box
          className="syseditor-paper-container"
          sx={{
            width: '100%',
            maxWidth: paperSize === 'a4' ? '210mm' : 
                      paperSize === 'a5' ? '148mm' : 
                      paperSize === 'cupom80' ? '80mm' : 
                      paperSize === 'cupom57' ? '57mm' : '100%',
            backgroundColor: 'white',
            boxShadow: paperSize !== 'auto' ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
            border: paperSize !== 'auto' ? '1px solid #e0e0e0' : 'none',
            minHeight: isFullscreen ? '100%' :
                       paperSize === 'a4' ? '297mm' : 
                       paperSize === 'a5' ? '210mm' : 
                       (height || 'auto'),
            height: 'auto',
            transition: 'max-width 0.3s ease, min-height 0.3s ease',
            position: 'relative',
            flexShrink: 0,
            m: paperSize !== 'auto' ? '0 auto' : 0,
            overflowX: 'hidden',
            overflowY: 'hidden',
            wordBreak: 'break-word',
            wordWrap: 'break-word',
            '& img': {
              maxWidth: '100%',
              height: 'auto'
            },
            '& table': {
              maxWidth: '100%'
            }
          }}
        >
          {/* Overlay de Drop */}
        {isDragOver && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'primary.main',
              opacity: 0.1,
              zIndex: 1000,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'primary.main',
                fontWeight: 'bold',
                textAlign: 'center',
                bgcolor: 'background.paper',
                px: 2,
                py: 1,
                borderRadius: 1,
                boxShadow: 2,
              }}
            >
              Solte aqui para inserir: {draggedVariable}
            </Typography>
          </Box>
        )}

        {isSourceCode ? (
          <Box sx={{ p: 0, minHeight: isFullscreen ? '100%' : (height || 'auto'), flexGrow: 1, display: 'flex' }}>
            <TextField
              multiline
              fullWidth
              value={editorState.content}
              onChange={(e) => handleContentChange(e.target.value)}
              disabled={disabled}
              variant="outlined"
              sx={{
                '& .MuiInputBase-root': {
                  height: '100%',
                  alignItems: 'flex-start',
                  fontFamily: 'monospace',
                  padding: '2mm',
                  overflowY: 'auto', // ativa o scroll vertical na área de texto
                },
                '& textarea': {
                  height: '100% !important',
                  fontFamily: 'monospace',
                  overflowY: 'auto', // ativa o scroll vertical no textarea
                  resize: 'vertical'
                }
              }}
              inputProps={{
                style: { minHeight: 200 }
              }}
            />
          </Box>
        ) : (
          <EditorContent
            ref={editorRef}
            content={editorState.content}
            placeholder={placeholder}
            height={height}
            fontStyle={fontStyle}
            onContentChange={handleContentChange}
            onSelectionChange={handleSelectionChange}
            disabled={disabled}
          />
        )}

        {/* Variables Section — dentro do container scrollável */}
        {showVariables && (
          <Paper
            variant="outlined"
            sx={{
              mt: 1,
              mx: 1,
              p: 1.5,
              bgcolor: 'action.hover',
              flexShrink: 0,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" fontWeight={600}>
                Variáveis Disponíveis
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.65rem' }}>
                <DragIndicator sx={{ fontSize: 14 }} />
                Arraste ou clique
              </Typography>
            </Box>

            {currentLoadingVariables ? (
              <Box display="flex" alignItems="center" gap={1} py={1}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Carregando variáveis...
                </Typography>
              </Box>
            ) : currentVariables.length > 0 ? (
              <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap>
                {currentVariables.map((variable, index) => {
                  const colors = getVariableColor(variable);
                  return (
                    <Chip
                      key={index}
                      label={variable}
                      variant="outlined"
                      clickable
                      size="small"
                      draggable
                      onClick={() => insertVariableAtCursor(variable)}
                      onDragStart={(e) => handleDragStart(e, variable)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        mb: 0.2,
                        fontSize: '0.65rem',
                        fontFamily: 'monospace',
                        cursor: 'grab',
                        userSelect: 'none',
                        bgcolor: colors.bg,
                        borderColor: colors.border,
                        color: '#546e7a',
                        fontWeight: 500,
                        '&:hover': {
                          bgcolor: colors.hover,
                          color: 'white',
                          borderColor: colors.hover,
                          '& .MuiChip-icon': { color: 'white' }
                        },
                        '&:active': { cursor: 'grabbing' },
                        '& .MuiChip-icon': { fontSize: '14px', marginLeft: '2px', color: '#78909c' }
                      }}
                    />
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary" py={0.5}>
                Nenhuma variável disponível para este tipo
              </Typography>
            )}
          </Paper>
        )}

        </Box>
      </Box>

      {/* Informações adicionais */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {editorState.content.length} caracteres
          </Typography>

          {/* Feedback visual do Ctrl+S */}
          {savePressed && onSave && (
            <Chip
              label="Ctrl+S pressionado - Salvando..."
              size="small"
              color="success"
              sx={{
                fontSize: '0.6rem',
                height: 20,
                animation: 'fadeInOut 1s ease-in-out'
              }}
            />
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {onSave && (
            <Typography variant="caption" color="text.secondary">
              Ctrl+S para salvar
            </Typography>
          )}
        </Box>
      </Box>

      <ImageUploadModal
        open={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        onImageInsert={handleImageInsert}
      />

      <AdvancedTableEditor
        open={isTableModalOpen}
        onClose={() => setIsTableModalOpen(false)}
        onTableInsert={handleTableInsert}
      />

      <ImageResizeModal
        open={isImageResizeModalOpen}
        onClose={() => setIsImageResizeModalOpen(false)}
        imageElement={selectedImage}
        onResize={handleImageResize}
      />

      <TablePropertiesModal
        open={isTablePropertiesModalOpen}
        onClose={() => setIsTablePropertiesModalOpen(false)}
        table={tableContextMenu?.table || null}
        onSave={handleTablePropertiesSave}
      />

      {/* Menu de contexto para tabelas */}
      <Menu
        open={tableContextMenu !== null}
        onClose={handleTableContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          tableContextMenu !== null
            ? { top: tableContextMenu.mouseY, left: tableContextMenu.mouseX }
            : undefined
        }
        sx={{
          zIndex: 10000,
          '& .MuiMenuItem-root': { fontSize: '0.85rem', minHeight: 33, py: 0.4, px: 1.4 },
          '& .MuiSvgIcon-root': { fontSize: 18 },
          '& .MuiDivider-root': { my: 0 },
        }}
      >
        <MenuItem
          onMouseEnter={(e) => handleSubmenuEnter('insert', e.currentTarget)}
          onMouseLeave={handleSubmenuLeave}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Add sx={{ mr: 0.8 }} />
            Inserir
          </Box>
          <ChevronRight />
        </MenuItem>
        <Divider />
        <MenuItem
          onMouseEnter={(e) => handleSubmenuEnter('delete', e.currentTarget)}
          onMouseLeave={handleSubmenuLeave}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Delete sx={{ mr: 0.8 }} />
            Excluir
          </Box>
          <ChevronRight />
        </MenuItem>
        <Divider />
        <MenuItem
          onMouseEnter={(e) => handleSubmenuEnter('merge', e.currentTarget)}
          onMouseLeave={handleSubmenuLeave}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CallMerge sx={{ mr: 0.8 }} />
            Célula
          </Box>
          <ChevronRight />
        </MenuItem>
        <Divider />
        <MenuItem
          onMouseEnter={(e) => handleSubmenuEnter('align', e.currentTarget)}
          onMouseLeave={handleSubmenuLeave}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormatAlignCenter sx={{ mr: 0.8 }} />
            Alinhamento
          </Box>
          <ChevronRight />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => setIsTablePropertiesModalOpen(true)}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Settings sx={{ mr: 0.8 }} />
            Propriedades
          </Box>
        </MenuItem>
        <MenuItem onClick={() => {
          if (tableContextMenu) {
            resetTableToFullWidth(tableContextMenu.table);
            handleTableContextMenuClose();
          }
        }}>
          <Box sx={{ mr: 0.8, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
            ↔️
          </Box>
          Resetar Largura
        </MenuItem>
      </Menu>

      {/* Submenus usando Popper */}
      {/* Submenu de Inserir */}
      <Popper
        open={hoveredSubmenu === 'insert' && submenuAnchor.element !== null}
        anchorEl={submenuAnchor.element}
        placement="right-start"
        style={{ zIndex: 10000 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 0],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={() => {
          setHoveredSubmenu(null);
          setSubmenuAnchor({ element: null, type: '' });
          if (submenuTimeoutRef.current) {
            clearTimeout(submenuTimeoutRef.current);
            submenuTimeoutRef.current = null;
          }
        }}>
          <Paper
            onMouseEnter={() => handleSubmenuStay('insert')}
            onMouseLeave={handleSubmenuLeave}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              '& .MuiMenuItem-root': { fontSize: '0.85rem', minHeight: 31, py: 0.3, px: 1.4 },
              '& .MuiSvgIcon-root': { fontSize: 17 },
              '& .MuiDivider-root': { my: 0 },
            }}
          >
            <MenuList dense>
              <MenuItem onClick={() => { insertTableRowAbove(); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}>
                <Add sx={{ mr: 0.8 }} /> Linha Acima
              </MenuItem>
              <MenuItem onClick={() => { insertTableRowBelow(); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}>
                <Add sx={{ mr: 0.8 }} /> Linha Abaixo
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { insertTableColumnLeft(); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}>
                <Add sx={{ mr: 0.8 }} /> Coluna à Esquerda
              </MenuItem>
              <MenuItem onClick={() => { insertTableColumnRight(); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}>
                <Add sx={{ mr: 0.8 }} /> Coluna à Direita
              </MenuItem>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Submenu de Mesclar */}
      <Popper
        open={hoveredSubmenu === 'merge' && submenuAnchor.element !== null}
        anchorEl={submenuAnchor.element}
        placement="right-start"
        style={{ zIndex: 10000 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 0],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={() => {
          setHoveredSubmenu(null);
          setSubmenuAnchor({ element: null, type: '' });
          if (submenuTimeoutRef.current) {
            clearTimeout(submenuTimeoutRef.current);
            submenuTimeoutRef.current = null;
          }
        }}>
          <Paper
            onMouseEnter={() => handleSubmenuStay('merge')}
            onMouseLeave={handleSubmenuLeave}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              '& .MuiMenuItem-root': { fontSize: '0.85rem', minHeight: 31, py: 0.3, px: 1.4 },
              '& .MuiSvgIcon-root': { fontSize: 17 },
              '& .MuiDivider-root': { my: 0 },
            }}
          >
            <MenuList dense>
              <MenuItem onClick={copyTableCell}>
                <ContentCopy sx={{ mr: 0.8 }} /> Copiar Célula
              </MenuItem>
              <MenuItem onClick={pasteTableCell} disabled={!copiedTableCell}>
                <ContentPaste sx={{ mr: 0.8 }} /> Colar Célula
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => { mergeSelectedCells(); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}
                disabled={!tableContextMenu || tableContextMenu.table.querySelectorAll('.syseditor-selected-cell').length < 2}
              >
                <CallMerge sx={{ mr: 0.8 }} /> Mesclar Selecionadas
              </MenuItem>
              <MenuItem
                onClick={() => { mergeTableCellRight(); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}
                disabled={!tableContextMenu || tableContextMenu.col >= tableContextMenu.table.rows[0].cells.length - 1}
              >
                <CallMerge sx={{ mr: 0.8 }} /> Mesclar com Direita
              </MenuItem>
              <MenuItem
                onClick={() => { mergeTableCellDown(); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}
                disabled={!tableContextMenu || tableContextMenu.row >= tableContextMenu.table.rows.length - 1}
              >
                <CallMerge sx={{ mr: 0.8 }} /> Mesclar com Abaixo
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => setIsCellPropertiesModalOpen(true)}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ mr: 0.8, width: 13, height: 13, border: '2px solid #1976d2', borderRadius: 0.5, backgroundColor: 'rgba(25, 118, 210, 0.1)' }} />
                  Propriedades da Célula
                </Box>
              </MenuItem>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Submenu de Alinhamento */}
      <Popper
        open={hoveredSubmenu === 'align' && submenuAnchor.element !== null}
        anchorEl={submenuAnchor.element}
        placement="right-start"
        style={{ zIndex: 10000 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 0],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={() => {
          setHoveredSubmenu(null);
          setSubmenuAnchor({ element: null, type: '' });
          if (submenuTimeoutRef.current) {
            clearTimeout(submenuTimeoutRef.current);
            submenuTimeoutRef.current = null;
          }
        }}>
          <Paper
            onMouseEnter={() => handleSubmenuStay('align')}
            onMouseLeave={handleSubmenuLeave}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              '& .MuiMenuItem-root': { fontSize: '0.85rem', minHeight: 31, py: 0.3, px: 1.4 },
              '& .MuiSvgIcon-root': { fontSize: 17 },
            }}
          >
            <MenuList dense>
              <MenuItem onClick={() => { setTableCellAlignment('left'); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}>
                <FormatAlignLeft sx={{ mr: 0.8 }} /> Esquerda
              </MenuItem>
              <MenuItem onClick={() => { setTableCellAlignment('center'); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}>
                <FormatAlignCenter sx={{ mr: 0.8 }} /> Centro
              </MenuItem>
              <MenuItem onClick={() => { setTableCellAlignment('right'); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}>
                <FormatAlignRight sx={{ mr: 0.8 }} /> Direita
              </MenuItem>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>





      {/* Submenu de Excluir */}
      <Popper
        open={hoveredSubmenu === 'delete' && submenuAnchor.element !== null}
        anchorEl={submenuAnchor.element}
        placement="right-start"
        style={{ zIndex: 10000 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 0],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={() => {
          setHoveredSubmenu(null);
          setSubmenuAnchor({ element: null, type: '' });
          if (submenuTimeoutRef.current) {
            clearTimeout(submenuTimeoutRef.current);
            submenuTimeoutRef.current = null;
          }
        }}>
          <Paper
            onMouseEnter={() => handleSubmenuStay('delete')}
            onMouseLeave={handleSubmenuLeave}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              '& .MuiMenuItem-root': { fontSize: '0.85rem', minHeight: 31, py: 0.3, px: 1.4 },
              '& .MuiSvgIcon-root': { fontSize: 17 },
            }}
          >
            <MenuList dense>
              <MenuItem
                onClick={() => { deleteTableRow(); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}
                disabled={!tableContextMenu || tableContextMenu.table.rows.length <= 1}
              >
                <Delete sx={{ mr: 0.8 }} /> Excluir Linha
              </MenuItem>
              <MenuItem
                onClick={() => { deleteTableColumn(); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}
                disabled={!tableContextMenu || tableContextMenu.table.rows[0].cells.length <= 1}
              >
                <Delete sx={{ mr: 0.8 }} /> Excluir Coluna
              </MenuItem>
              <MenuItem
                onClick={() => { deleteTable(); setHoveredSubmenu(null); setSubmenuAnchor({ element: null, type: '' }); }}
              >
                <Delete sx={{ mr: 0.8 }} /> Excluir Tabela
              </MenuItem>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Modal de IA */}
      <AiModal
        open={isAiModalOpen}
        onClose={handleAiModalClose}
        onGenerate={handleAiGenerate}
        onInsert={handleInsertAiResponse}
        prompt={aiPrompt}
        setPrompt={setAiPrompt}
        response={aiResponse}
        setResponse={setAiResponse}
        isLoading={isAiLoading}
        error={aiError}
      />

      {/* Modal de Ajuda */}
      <HelpModal 
        open={isHelpModalOpen} 
        onClose={() => setIsHelpModalOpen(false)} 
      />

      {/* Modal de Propriedades da Célula */}
      <CellPropertiesModal
        open={isCellPropertiesModalOpen}
        onClose={() => setIsCellPropertiesModalOpen(false)}
        setCellBorderLeft={setCellBorderLeft}
        setCellBorderRight={setCellBorderRight}
        setCellBorderTop={setCellBorderTop}
        setCellBorderBottom={setCellBorderBottom}
        removeCellBorderLeft={removeCellBorderLeft}
        removeCellBorderRight={removeCellBorderRight}
        removeCellBorderTop={removeCellBorderTop}
        removeCellBorderBottom={removeCellBorderBottom}
        setCellBorderStyle={setCellBorderStyle}
        setCellBorderWidth={setCellBorderWidth}
        setCellBorderColor={setCellBorderColor}
        removeCellBorders={removeCellBorders}
        setCellBackgroundColor={setTableCellBackgroundColor}
      />




    </Box>
  );
};

export default SysEditor; 